import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import { MotionConfig, useReducedMotion } from 'framer-motion';
import { Toaster } from 'sonner';
import Lenis from 'lenis';
import { Navbar, Footer } from './components/Layout';
import Home from './pages/Home';
import Services from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import Team from './pages/Team';
import IntakeForm from './pages/AiDiscovery';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CaseStudies from './pages/CaseStudies';
import CaseStudyDetail from './pages/CaseStudyDetail';
import Careers from './pages/Careers';
import CareerDetails from './pages/CareerDetails';
import AdminDiscovery from './pages/AdminDiscovery';
import AdminCareers from './pages/AdminCareers';
import AdminOpportunities from './pages/AdminOpportunities';
import AdminLogin from './pages/AdminLogin';
import PolicyPage from './pages/PolicyPage';
import './App.css';
import './Brand.css';
import './Team.css';
import './Careers.css';
import './ProfessionalTheme.css';
import './NavigationMap.css';
import './QuietLayout.css';
import './LocalizationMetrics.css';
import './Typography.css';
import './Discovery.css';
import './Admin.css';
import './Content.css';
import { LocaleProvider, L, useLocale } from './i18n/LocaleProvider';
import { company } from './data/content';

const titles = {
  '/': "TechSpitze — Built for What's Next",
  '/services': 'Our Services — TechSpitze',
  '/about': 'About Us — TechSpitze',
  '/our-team': 'Our Team — TechSpitze',
  '/our-team/': 'Our Team — TechSpitze',
  '/careers': 'Careers — TechSpitze',
  '/careers/': 'Careers — TechSpitze',
  '/contact': 'Let’s Talk — TechSpitze',
  '/intake-form': 'Intake Form — TechSpitze',
  '/blog': 'Insights — TechSpitze',
  '/case-studies': 'Case Studies — TechSpitze',
  '/privacy-policy': 'Privacy Policy — TechSpitze',
  '/terms': 'Terms of Service — TechSpitze',
  '/cookie-policy': 'Cookie Policy — TechSpitze',
  '/accessibility': 'Accessibility Statement — TechSpitze',
  '/admin/discovery': 'Intake Form Submissions — TechSpitze',
  '/admin/careers': 'Candidate Applications — TechSpitze',
  '/admin/opportunities': 'Career Opportunities — TechSpitze',
  '/admin/login': 'Team Sign In — TechSpitze',
};
const selfTitled = path => /^\/(blog|case-studies)\/.+/.test(path) || ['/privacy-policy', '/terms', '/cookie-policy', '/accessibility', '/', '/services'].includes(path);

const PageExperience = () => {
  const { t } = useLocale();
  const { pathname, hash } = useLocation();
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, syncTouch: false, anchors: false, autoRaf: true });
    window.__techspitzeLenis = lenis;
    return () => { lenis.destroy(); delete window.__techspitzeLenis; };
  }, [reduced]);
  useEffect(() => {
    if (selfTitled(pathname)) return;
    document.title = t(titles[pathname.replace(/\/$/, '') || '/'] || 'Page Not Found — TechSpitze');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t(company.marketingStatement));
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', t(company.marketingStatement));
  }, [pathname, t]);
  useEffect(() => {
    let firstFrame, secondFrame;
    firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
        if (window.__techspitzeLenis) window.__techspitzeLenis.scrollTo(target || 0, { immediate: true, offset: target ? -120 : 0, force: true });
        else if (target) target.scrollIntoView({ block: 'start' });
        else window.scrollTo(0, 0);
      });
    });
    return () => { cancelAnimationFrame(firstFrame); cancelAnimationFrame(secondFrame); };
  }, [pathname, hash]);
  return null;
};

const NotFound = () => <L><main id="main-content" className="container not-found" data-testid="not-found-page"><span className="eyebrow" data-testid="not-found-code">404 / OFF THE BEATEN PATH</span><h1 data-testid="not-found-heading">Let’s get you<br /><span className="heading-accent">back on track.</span></h1><p className="page-description" data-testid="not-found-description">That page has moved or never existed. Try one of these instead.</p><div className="hero-actions"><Link className="action-button button-primary" to="/" data-testid="not-found-home">Back to Home ↗</Link><Link className="action-button button-secondary" to="/services" data-testid="not-found-services">Explore Services</Link><Link className="action-button button-secondary" to="/contact" data-testid="not-found-contact">Contact us</Link></div></main></L>;

const SiteChrome = ({ children }) => (useLocation().pathname.startsWith('/admin') ? null : children);

function App() {
  return (
    <LocaleProvider>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <PageExperience />
          <L>
            <a href="#main-content" className="skip-link" data-testid="skip-to-content">
              Skip to content
            </a>
          </L>
          <div className="site-ambiance" aria-hidden="true">
            <div className="ambient-orb orb-teal" />
            <div className="ambient-orb orb-gold" />
          </div>
          <SiteChrome>
            <Navbar />
          </SiteChrome>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/our-team" element={<Team />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:id" element={<CareerDetails />} />
            <Route path="/intake-form" element={<IntakeForm />} />
            <Route path="/ai-discovery" element={<Navigate to="/intake-form" replace />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/privacy-policy" element={<PolicyPage slug="privacy-policy" />} />
            <Route path="/terms" element={<PolicyPage slug="terms" />} />
            <Route path="/cookie-policy" element={<PolicyPage slug="cookie-policy" />} />
            <Route path="/accessibility" element={<PolicyPage slug="accessibility" />} />
            <Route path="/admin" element={<Navigate to="/admin/discovery" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/discovery" element={<AdminDiscovery />} />
            <Route path="/admin/careers" element={<AdminCareers />} />
            <Route path="/admin/opportunities" element={<AdminOpportunities />} />
            <Route path="/admin/*" element={<Navigate to="/admin/discovery" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <SiteChrome>
            <Footer />
          </SiteChrome>
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </MotionConfig>
    </LocaleProvider>
  );
}

export default App;